import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

/**
 * AuthContext — single source of truth for the signed-in user.
 *
 * Resilience improvements (iOS Safari ITP + cross-tab sync):
 *  • Bearer token saved to localStorage on every successful login flow,
 *    so axios can fall back to `Authorization: Bearer …` when the HttpOnly
 *    cookie is dropped by the browser.
 *  • Window focus / visibilitychange triggers a quiet /auth/me re-validation,
 *    so a tab left open overnight catches expired sessions instead of
 *    silently 401-ing on the first action.
 *  • Listens for the global `ctx:unauthorized` event emitted by the api.js
 *    interceptor; on stale-session 401, resets the in-memory user and lets
 *    the next ProtectedRoute redirect the user to /account/login.
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);
    // Suppress focus-revalidation thrash on fast tab switches.
    const lastRevalidatedAt = useRef(0);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            setUser(null);
            return null;
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Re-validate session when the tab regains focus. Throttled to once
    // every 60s so power users tab-switching constantly aren't slammed.
    useEffect(() => {
        const REVALIDATE_INTERVAL_MS = 60 * 1000;
        const onFocus = () => {
            if (typeof document !== "undefined" && document.hidden) return;
            const now = Date.now();
            if (now - lastRevalidatedAt.current < REVALIDATE_INTERVAL_MS) return;
            lastRevalidatedAt.current = now;
            refresh();
        };
        const onUnauthorized = () => {
            // Stale token detected by api.js → clear UI state immediately.
            setUser(null);
        };
        window.addEventListener("focus", onFocus);
        window.addEventListener("visibilitychange", onFocus);
        window.addEventListener("ctx:unauthorized", onUnauthorized);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("visibilitychange", onFocus);
            window.removeEventListener("ctx:unauthorized", onUnauthorized);
        };
    }, [refresh]);

    // Admin password login (kept for legacy /admin/login)
    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.token) localStorage.setItem("ctx_token", data.token);
        setUser(data.user);
        return data.user;
    };

    /**
     * Hydrate AuthContext immediately after a successful login, BEFORE the
     * calling page navigates to /dashboard. Avoids the first-paint flash
     * where the Dashboard reads user===null until its mount-time refresh()
     * resolves.
     *
     * Also persists the Bearer token to localStorage if provided — this is
     * the iOS Safari ITP fallback for when the HttpOnly cookie is dropped.
     */
    const hydrateFromCustomerLogin = useCallback((customerUserPayload, token) => {
        if (token) {
            try { localStorage.setItem("ctx_token", token); } catch { /* ignore */ }
        }
        if (!customerUserPayload) return;
        setUser({
            id: customerUserPayload.id,
            email: customerUserPayload.email,
            name: customerUserPayload.name || null,
            role: customerUserPayload.role || "user",
            is_member: !!customerUserPayload.is_member,
            membership_plan: customerUserPayload.membership_plan || null,
            shopify_tags: customerUserPayload.shopify_tags || [],
        });
        setChecking(false);
        // Kick off a full /auth/me in the background to merge any fields
        // the login response didn't include (subscription_status, etc.).
        setTimeout(() => { refresh(); }, 50);
    }, [refresh]);

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // ignore
        }
        try {
            await api.post("/customer/logout");
        } catch (e) {
            // ignore
        }
        try { localStorage.removeItem("ctx_token"); } catch { /* ignore */ }
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, checking, login, logout, refresh, hydrateFromCustomerLogin }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
